import { yupResolver } from "@hookform/resolvers/yup";
import { useRouter } from "expo-router";
import { useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { Keyboard, useWindowDimensions } from "react-native";
import { useDebouncedCallback } from "use-debounce";
import * as yup from "yup";

import { useCurrentUser } from "../../providers/CurrentUser";
import RegisterView, {
  IForm,
  makeFirstNameField,
  makeLastNameField,
  makeTitleField,
} from "./RegisterView";

import { useAuthUpdateMyProfileMutation } from "@/generated/graphql";
import useRecordUserGoal, { GoalType } from "@/hooks/useRecordUserGoal";
import { useTheme } from "@/providers/ThemeProvider";
import { useAuth } from "../../providers/Auth";

/**
 * NativeWind `md:` breakpoint (768px). The auth content panel fixes its width
 * at md+ and otherwise fills the viewport — a screen-width decision derived
 * from the measured window width rather than the vendored Gluestack breakpoint hook.
 */
const MD_BREAKPOINT = 768;

const schema = yup.object().shape({
  firstName: yup.string().required("Can't be blank"),
  lastName: yup.string().required("Can't be blank"),
  title: yup.string().required("Can't be blank"),
});

/**
 * Container component for user registration/profile completion
 *
 * Handles form state, validation, and profile update mutation.
 * Clears validation codes via onCompleteRegistration when profile is successfully updated.
 */
const RegisterContainer = () => {
  const auth = useAuth();
  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
  } = useForm<IForm>({
    mode: "onChange",
    resolver: yupResolver(schema),
    delayError: 1000,
  });
  const currentUser = useCurrentUser();
  const userGoal = useRecordUserGoal({
    goalType: GoalType.UpdateProfile,
    value: currentUser?.id ?? "",
  });
  const [updateProfile] = useAuthUpdateMyProfileMutation();
  const router = useRouter();

  const submit = useCallback(
    ({ firstName, lastName, title }: IForm) => {
      Keyboard.dismiss();
      return updateProfile({
        onCompleted: () => {
          userGoal();
          auth?.onCompleteRegistration();
          setTimeout(() => router.replace("/"), 1000);
        },
        onError: console.log,
        variables: { input: { firstName, lastName, title } },
        refetchQueries: ["AuthMe"],
        awaitRefetchQueries: true,
        optimisticResponse: {
          __typename: "Mutation",
          updateMyProfile: {
            ...currentUser!,
            title,
            cognitoUser: {
              ...currentUser!.cognitoUser!,
              UserAttributes: [
                { Name: "given_name", Value: firstName },
                { Name: "family_name", Value: lastName },
              ],
              Attributes: [
                { Name: "given_name", Value: firstName },
                { Name: "family_name", Value: lastName },
              ],
            },
          },
        },
      });
    },
    [auth, currentUser, router, updateProfile, userGoal]
  );

  const doSubmit = handleSubmit(submit);
  const handleDebounceSubmit = useDebouncedCallback(doSubmit, 200);
  const { width } = useWindowDimensions();
  const widthContent = width >= MD_BREAKPOINT ? 750 : width;
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  // Memoized because `Controller` re-renders a field whenever its `render`
  // changes identity — which is what the View's in-JSX `useCallback`s were for.
  // One options object, three fields: they all read the same three values.
  const fieldOptions = useMemo(
    () => ({ errors, isDark, loading: isSubmitting }),
    [errors, isDark, isSubmitting]
  );
  const renderFirstName = useMemo(
    () => makeFirstNameField(fieldOptions),
    [fieldOptions]
  );
  const renderLastName = useMemo(
    () => makeLastNameField(fieldOptions),
    [fieldOptions]
  );
  const renderTitle = useMemo(
    () => makeTitleField(fieldOptions),
    [fieldOptions]
  );

  return (
    <RegisterView
      valid={isValid}
      widthContent={widthContent}
      loading={isSubmitting}
      control={control}
      onSubmit={handleDebounceSubmit}
      isDark={isDark}
      renderFirstName={renderFirstName}
      renderLastName={renderLastName}
      renderTitle={renderTitle}
    />
  );
};

export default RegisterContainer;
